/**
 * WF-Option - Individual option component for wizard forms
 *
 * A selectable option card with slot content and auto-injected shortcut badge.
 *
 * @example
 * <wf-option value="developer">
 *   <strong>Developer</strong>
 *   <span>I write code</span>
 * </wf-option>
 */

import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { wfOptionStyles } from './wf-option.styles.js';

// Import wf-badge for shortcut display
import './wf-badge.js';

// Event detail types
export interface OptionSelectDetail {
  value: string;
  selected: boolean;
}

@customElement('wf-option')
export class WfOption extends LitElement {
  static styles = wfOptionStyles;

  /**
   * The value associated with this option (required)
   */
  @property({ type: String })
  value = '';

  /**
   * Whether this option is selected
   */
  @property({ type: Boolean, reflect: true })
  selected = false;

  /**
   * Whether this option is disabled
   */
  @property({ type: Boolean, reflect: true })
  disabled = false;

  /**
   * The keyboard shortcut key for this option (auto-assigned by parent)
   */
  @property({ type: String })
  shortcut = '';

  /**
   * Internal state for selection animation
   */
  @state()
  private _selecting = false;

  render() {
    return html`
      <button
        class="wf-option-card ${this._selecting ? 'selecting' : ''}"
        role="option"
        aria-selected="${this.selected}"
        ?disabled="${this.disabled}"
        data-testid="wf-option-btn"
        @click="${this._handleClick}"
      >
        ${this.shortcut
          ? html`<wf-badge
              shortcut="${this.shortcut}"
              variant="${this.selected ? 'selected' : 'default'}"
            ></wf-badge>`
          : nothing}
        <div class="wf-option-content">
          <slot></slot>
        </div>
      </button>
    `;
  }

  private _handleClick(e: Event): void {
    if (this.disabled) {
      e.preventDefault();
      return;
    }

    this._triggerSelect();
  }

  /**
   * Programmatically trigger selection (used by keyboard shortcuts)
   */
  triggerSelect(): void {
    if (!this.disabled) {
      this._triggerSelect();
    }
  }

  private _triggerSelect(): void {
    // Start selection animation
    this._selecting = true;

    // Dispatch selection event
    const detail: OptionSelectDetail = {
      value: this.value,
      selected: !this.selected, // Toggle for multi-select, parent handles logic
    };

    this.dispatchEvent(
      new CustomEvent('wf-option-select', {
        detail,
        bubbles: true,
        composed: true,
      })
    );

    // Clear animation after it completes
    setTimeout(() => {
      this._selecting = false;
    }, 300);
  }

  /**
   * Focus the option button
   */
  focus(): void {
    const button = this.shadowRoot?.querySelector('button');
    button?.focus();
  }

  /**
   * Set selected state programmatically
   */
  setSelected(selected: boolean): void {
    this.selected = selected;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-option': WfOption;
  }
}
